import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Table,
  Badge,
} from "reactstrap";
import { injectIntl } from "react-intl";
import axios from "axios";
import { NotificationManager } from "../../../../components/common/react-notifications";
import { END_POINT } from "../../../../constants/defaultValues";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const StockReturnForm = ({
  intl,
  isOpen,
  toggle,
  onSuccess,
  stocks = [],
  providers = [],
}) => {
  const [returnDate, setReturnDate] = useState(moment());
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setReturnDate(moment());
    setReason("");
    setNotes("");
    setItems([]);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  const addItem = (stock) => {
    // Kiểm tra đã có trong danh sách chưa
    if (items.find((i) => i.stock_id === stock.id)) {
      NotificationManager.warning("Nguyên liệu đã được thêm", "Cảnh báo");
      return;
    }

    // Tìm provider có nguyên liệu này trong company_provider_item
    let matchedProviderId = "";
    for (const provider of providers) {
      const providerItems = provider.company_provider_item || [];
      const found = providerItems.find((item) => item.stock_id === stock.id);
      if (found) {
        matchedProviderId = provider.id;
        break;
      }
    }

    // Nếu không tìm thấy, dùng provider đầu tiên nếu chỉ có 1
    if (!matchedProviderId && providers.length === 1) {
      matchedProviderId = providers[0].id;
    }

    setItems([
      ...items,
      {
        stock_id: stock.id,
        stock_name: stock.name,
        stock_unit: stock.unit,
        quantity: 1,
        unit_price: stock.avarage_price || 0,
        current_count: stock.count,
        provider_id: matchedProviderId,
      },
    ]);
  };

  const removeItem = (stockId) => {
    setItems(items.filter((i) => i.stock_id !== stockId));
  };

  const updateItemQuantity = (stockId, quantity) => {
    setItems(
      items.map((i) =>
        i.stock_id === stockId
          ? { ...i, quantity: parseFloat(quantity) || 0 }
          : i
      )
    );
  };

  const updateItemPrice = (stockId, price) => {
    setItems(
      items.map((i) =>
        i.stock_id === stockId
          ? { ...i, unit_price: parseFloat(price) || 0 }
          : i
      )
    );
  };

  const updateItemProvider = (stockId, providerId) => {
    setItems(
      items.map((i) =>
        i.stock_id === stockId ? { ...i, provider_id: providerId } : i
      )
    );
  };

  const getTotalValue = () => {
    return items.reduce(
      (sum, item) => sum + item.quantity * item.unit_price,
      0
    );
  };

  const handleSubmit = async () => {
    if (!reason || reason.trim() === "") {
      NotificationManager.warning("Vui lòng nhập lý do hoàn hàng", "Cảnh báo");
      return;
    }

    if (items.length === 0) {
      NotificationManager.warning(
        "Vui lòng thêm nguyên liệu cần hoàn",
        "Cảnh báo"
      );
      return;
    }

    // Kiểm tra số lượng
    for (const item of items) {
      if (item.quantity <= 0) {
        NotificationManager.warning("Số lượng phải lớn hơn 0", "Cảnh báo");
        return;
      }
      if (item.quantity > item.current_count) {
        NotificationManager.warning(
          `Số lượng hoàn của "${item.stock_name}" vượt quá tồn kho`,
          "Cảnh báo"
        );
        return;
      }
    }

    setLoading(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/api/stock-return`,
        {
          return_date: moment(returnDate).format("YYYY-MM-DD"),
          reason,
          notes,
          // provider_id: providerId || null,
          items: items.map((i) => ({
            stock_id: i.stock_id,
            quantity: i.quantity,
            unit_price: i.unit_price,
            provider_id: i.provider_id || null,
          })),
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (response.data.status === 200) {
        NotificationManager.success(
          "Tạo phiếu hoàn hàng thành công",
          "Thành công"
        );

        // Hoàn thành ngay sau khi tạo
        const returnId = response.data.data.id;
        const completeResponse = await axios.post(
          `${END_POINT}/api/stock-return/${returnId}/complete`,
          {},
          {
            headers: { Authorization: `Bearer ${tokenStr}` },
          }
        );

        if (completeResponse.data.status === 200) {
          NotificationManager.success(
            "Hoàn hàng thành công, tồn kho đã được cập nhật",
            "Thành công"
          );
        }

        onSuccess && onSuccess();
        handleClose();
      } else {
        NotificationManager.error(response.data.responseText, "Lỗi");
      }
    } catch (error) {
      NotificationManager.error("Có lỗi xảy ra", "Lỗi");
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("vi-VN").format(value || 0);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      style={{ maxWidth: "90vw", width: "90vw" }}
    >
      <ModalHeader toggle={handleClose}>Hoàn Hàng Nhà Cung Cấp</ModalHeader>
      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label>Ngày hoàn hàng</Label>
              <DatePicker
                selected={returnDate ? moment(returnDate) : null}
                onChange={(date) => setReturnDate(date)}
                dateFormat="DD-MM-YYYY"
                className="form-control"
              />
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label>
                Lý do hoàn <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Hàng lỗi, hết hạn..."
              />
            </FormGroup>
          </div>
        </div>

        {/* Chọn nguyên liệu */}
        <FormGroup>
          <Label>Chọn nguyên liệu cần hoàn</Label>
          <Input
            type="select"
            onChange={(e) => {
              const stock = stocks.find(
                (s) => s.id === parseInt(e.target.value)
              );
              if (stock) addItem(stock);
              e.target.value = "";
            }}
          >
            <option value="">-- Chọn nguyên liệu --</option>
            {stocks
              .filter((s) => !items.find((i) => i.stock_id === s.id))
              .map((stock) => (
                <option key={stock.id} value={stock.id}>
                  {stock.name} (Tồn: {stock.count} {stock.unit})
                </option>
              ))}
          </Input>
        </FormGroup>

        {/* Danh sách nguyên liệu đã chọn */}
        {items.length > 0 && (
          <Table size="sm" responsive>
            <thead>
              <tr>
                <th>Nguyên liệu</th>
                <th className="text-center">Tồn kho</th>
                <th className="text-center" style={{ width: "100px" }}>
                  Số lượng hoàn
                </th>
                <th className="text-center" style={{ width: "120px" }}>
                  Đơn giá
                </th>
                <th className="text-center" style={{ width: "240px" }}>
                  Nhà cung cấp
                </th>
                <th className="text-right" style={{ width: "120px" }}>
                  Thành tiền
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.stock_id}>
                  <td style={{ verticalAlign: "middle" }}>{item.stock_name}</td>
                  <td
                    className="text-center"
                    style={{ verticalAlign: "middle" }}
                  >
                    {item.current_count} {item.stock_unit}
                  </td>
                  <td
                    className="text-center"
                    style={{ verticalAlign: "middle" }}
                  >
                    <Input
                      type="number"
                      bsSize="sm"
                      value={item.quantity}
                      onChange={(e) =>
                        updateItemQuantity(item.stock_id, e.target.value)
                      }
                      min="0"
                      max={item.current_count}
                    />
                  </td>
                  <td
                    className="text-center"
                    style={{ verticalAlign: "middle" }}
                  >
                    <Input
                      type="number"
                      bsSize="sm"
                      value={item.unit_price}
                      onChange={(e) =>
                        updateItemPrice(item.stock_id, e.target.value)
                      }
                      min="0"
                    />
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {providers.length === 1 ? (
                      <span className="text-muted">{providers[0].name}</span>
                    ) : (
                      <Input
                        type="select"
                        bsSize="sm"
                        value={item.provider_id || ""}
                        onChange={(e) =>
                          updateItemProvider(item.stock_id, e.target.value)
                        }
                      >
                        <option value="">-- Chọn NCC --</option>
                        {providers.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Input>
                    )}
                  </td>
                  <td
                    className="text-right"
                    style={{ verticalAlign: "middle" }}
                  >
                    {formatCurrency(item.quantity * item.unit_price)}
                  </td>
                  <td
                    className="text-center"
                    style={{ verticalAlign: "middle" }}
                  >
                    <Button
                      color="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => removeItem(item.stock_id)}
                    >
                      <i className="simple-icon-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" className="text-right">
                  <strong>Tổng giá trị:</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(getTotalValue())} đ</strong>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </Table>
        )}

        <FormGroup>
          <Label>Ghi chú</Label>
          <Input
            type="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Hủy
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Đang xử lý..." : "Hoàn hàng"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default injectIntl(StockReturnForm);
